import axios from 'axios';

// Base URL from env (legacy variable name maintained for compatibility)
const baseURL = process.env.REACT_APP_PUBLIC_PATH || process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Axios instance
const api = axios.create({
  baseURL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor: attach access token
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      // Ensure Bearer prefix
      config.headers.Authorization = accessToken.startsWith('Bearer ') 
        ? accessToken 
        : `Bearer ${accessToken}`;
    } else {
      // Fallback to legacy single token if present
      const legacyToken = localStorage.getItem('token');
      if (legacyToken) {
        config.headers.Authorization = legacyToken.startsWith('Bearer ')
          ? legacyToken
          : `Bearer ${legacyToken}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Shared in-flight refresh so concurrent 401s wait on one refresh call instead of each racing their own
let refreshPromise = null;

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token available');
  const refreshResponse = await axios.post(`${baseURL}/auth/refresh-token`, { refreshToken }, { timeout: 15000 });
  const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data;
  if (accessToken) localStorage.setItem('accessToken', accessToken);
  if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
  return accessToken;
}

// Response interceptor: refresh token on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Only attempt refresh for auth failures and not already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const accessToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshErr) {
        // Clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('admin');
        window.location.href = '/authentication/sign-in';
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
